import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Package, MoreVertical } from 'lucide-react'

const CURRENCY_SYMBOLS = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh', ZAR:'R' }
const STATUS_LABELS = { pending:'Unpaid', paid:'Paid', collected:'Collected' }

export default function AmbassadorPage() {
  const { token } = useParams()
  const [ambassador, setAmbassador] = useState(null)
  const [wedding,    setWedding]    = useState(null)
  const [asoEbi,     setAsoEbi]     = useState(null)
  const [orders,     setOrders]     = useState([])
  const [points,     setPoints]     = useState([])
  const [loading,    setLoading]    = useState(true)
  const [invalid,    setInvalid]    = useState(false)

  // Filters
  const [pointFilter,  setPointFilter]  = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // 3-dot menu
  const [openMenu,  setOpenMenu]  = useState(null)
  const [menuPos,   setMenuPos]   = useState({ top:0, right:0 })

  // PIN gate
  const [needsPin,  setNeedsPin]  = useState(false)
  const [pin,       setPin]       = useState('')
  const [pinErr,    setPinErr]    = useState('')
  const [storedPin, setStoredPin] = useState('')
  const pinRef = useRef(null)

  useEffect(() => {
    async function load() {
      if (!token) { setInvalid(true); setLoading(false); return }

      const { data: amb } = await supabase
        .from('asoebi_ambassadors').select('*').eq('access_token', token).maybeSingle()
      if (!amb) { setInvalid(true); setLoading(false); return }
      setAmbassador(amb)
      setStoredPin(amb.access_pin || '')

      // Check PIN
      const sessionKey = `ambassador_${token}`
      if (amb.access_pin && sessionStorage.getItem(sessionKey) !== 'ok') {
        const { data: wPreview } = await supabase.rpc('get_public_wedding', { p_id: amb.wedding_id })
        setWedding(wPreview)
        setNeedsPin(true)
        setLoading(false)
        setTimeout(() => pinRef.current?.focus(), 100)
        return
      }

      await loadData(amb.wedding_id)
    }
    load()
  }, [token])

  // Keep orders fresh: realtime (works while RLS is off) + a 25s poll and a
  // refetch on tab-focus (covers the case where RLS blocks anon realtime).
  useEffect(() => {
    if (!ambassador?.wedding_id || needsPin) return
    const wid = ambassador.wedding_id
    const refresh = () => loadData(wid)
    const channel = supabase.channel(`amb-orders-${wid}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'asoebi_orders', filter:`wedding_id=eq.${wid}` }, refresh)
      .subscribe()
    const id = setInterval(refresh, 25000)
    window.addEventListener('focus', refresh)
    return () => { supabase.removeChannel(channel); clearInterval(id); window.removeEventListener('focus', refresh) }
  }, [ambassador?.wedding_id, needsPin])

  async function loadData(weddingId) {
    const [wRes, aeRes, oRes, pRes] = await Promise.all([
      supabase.rpc('get_public_wedding', { p_id: weddingId }),
      supabase.from('asoebi_setup').select('*').eq('wedding_id', weddingId).maybeSingle(),
      supabase.rpc('get_ambassador_orders', { p_token: token }),   // token-gated read
      supabase.from('asoebi_collection_points').select('*').eq('wedding_id', weddingId).order('created_at'),
    ])
    setWedding(wRes.data)
    setAsoEbi(aeRes.data)
    // Prefer the token-gated RPC; if it isn't deployed yet (data null), fall back
    // to a direct read so orders still show during the transition.
    let ords = Array.isArray(oRes.data) ? oRes.data : null
    if (ords === null) {
      const direct = await supabase.from('asoebi_orders').select('*').eq('wedding_id', weddingId).order('created_at', { ascending:false })
      ords = direct.data || []
    }
    setOrders(ords)
    setPoints(pRes.data || [])
    setLoading(false)
  }

  // Ambassadors can ONLY toggle collection (paid <-> collected), via the
  // token-verified function. They can never set/undo "paid" (Paystack only).
  async function updateStatus(order, action) { // action: 'collect' | 'uncollect'
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ambassador-order-status`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', apikey:import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization:`Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ access_token: token, order_id: order.id, action }),
      })
      const j = await res.json()
      if (j.ok) setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: j.status } : o))
    } catch(_) {}
    setOpenMenu(null)
  }

  function exportCSV() {
    const rows = [['Guest','Phone','Yards','Amount','Status','Collection Point','Date']]
    orders.forEach(o => rows.push([
      o.guest_name, o.guest_phone||'', o.yards, o.amount||0,
      STATUS_LABELS[o.status]||o.status, o.collection_point||'', new Date(o.created_at).toLocaleDateString('en-GB')
    ]))
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n')
    const a = document.createElement('a')
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    a.download = 'asoebi-orders.csv'
    a.click()
  }

  function handleMenu(id, e) {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    setOpenMenu(prev => prev === id ? null : id)
  }

  function submitPin() {
    if (pin === storedPin) {
      sessionStorage.setItem(`ambassador_${token}`, 'ok')
      setNeedsPin(false)
      setPinErr('')
      setLoading(true)
      loadData(ambassador.wedding_id)
    } else {
      setPinErr('Incorrect PIN. Check the message from the couple.')
      setPin('')
    }
  }

  function waLink(phone, name) {
    const clean = phone.replace(/[\s\-+()]/g,'').replace(/^0/,'234')
    const msg = `Hi ${name||'there'}, this is a reminder about your Aso Ebi order for ${wedding?.partner1_name} & ${wedding?.partner2_name}'s wedding.`
    return `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`
  }

  function waCollect(phone, name, point) {
    const clean = phone.replace(/[\s\-+()]/g,'').replace(/^0/,'234')
    const where = point ? ` at ${point}` : ''
    const msg = `Hi ${name||'there'}, your Aso Ebi for ${wedding?.partner1_name} & ${wedding?.partner2_name}'s wedding is ready for pickup${where}. Please come and collect it. Thank you! 🧵`
    return `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`
  }

  // ── PIN SCREEN ─────────────────────────────────────────────
  if (needsPin) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
      <div style={{ width:'100%', maxWidth:340, textAlign:'center' }}>
        <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:40, objectFit:'contain', display:'block', margin:'0 auto 1.25rem' }}/>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', color:'#1A1A1A', margin:'0 0 0.35rem' }}>
          {wedding?.partner1_name} &amp; {wedding?.partner2_name}
        </h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:'0 0 1.5rem' }}>
          Enter the 4-digit PIN to access Aso Ebi orders
        </p>
        <input
          ref={pinRef} type="text" inputMode="numeric" maxLength={4}
          value={pin}
          onChange={e => { setPin(e.target.value.replace(/\D/g,'')); setPinErr('') }}
          onKeyDown={e => { if (e.key === 'Enter' && pin.length === 4) submitPin() }}
          placeholder="• • • •"
          style={{ width:'100%', maxWidth:180, fontFamily:'Nunito Sans, sans-serif', fontSize:'1.5rem', fontWeight:700, textAlign:'center', letterSpacing:'0.5em', padding:'0.75rem 1rem', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', outline:'none', boxSizing:'border-box', margin:'0 auto', display:'block' }}
        />
        {pinErr && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#DC2626', margin:'0.75rem 0 0' }}>{pinErr}</p>}
        <button onClick={submitPin} disabled={pin.length !== 4}
          style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', color:'white', background: pin.length === 4 ? 'linear-gradient(135deg,#1A1A1A,#2A1810)' : '#D4C4BC', border:'none', borderRadius:100, padding:'0.75rem 2rem', cursor: pin.length === 4 ? 'pointer' : 'default', marginTop:'1rem', transition:'background 0.15s' }}>
          Verify
        </button>
      </div>
    </div>
  )

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading…</p>
    </div>
  )

  if (invalid) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'2rem' }}>
      <div>
        <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:36, objectFit:'contain', display:'block', margin:'0 auto 1rem' }}/>
        <p style={{ fontFamily:'Gibs, serif', fontSize:'1.4rem', color:'#1A1A1A', marginBottom:'0.5rem' }}>Invalid ambassador link</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC', fontSize:'0.875rem', maxWidth:320, margin:'0 auto' }}>
          This link is no longer valid. Ask the couple for a new link.
        </p>
      </div>
    </div>
  )

  const cs = CURRENCY_SYMBOLS[wedding?.currency] || '₦'
  const isPaid = o => o.status === 'paid' || o.status === 'collected'

  // Coordinators only deal with guests who have paid (payment is automatic), so
  // the page shows paid orders only — filters are All / Collected / Not Collected.
  const paidOrders = orders.filter(isPaid)
  let filtered = paidOrders
  if (pointFilter !== 'all') filtered = filtered.filter(o => o.collection_point === pointFilter)
  if (statusFilter === 'collected')     filtered = filtered.filter(o => o.status === 'collected')
  if (statusFilter === 'not_collected') filtered = filtered.filter(o => o.status === 'paid')

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', fontFamily:'Nunito Sans, sans-serif' }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#1A1A1A,#2A1810)', padding:'1.25rem 1.5rem' }}>
        <div style={{ maxWidth:520, margin:'0 auto' }}>
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:28, objectFit:'contain', filter:'brightness(10)', display:'block', marginBottom:'0.75rem' }}/>
          <p style={{ fontSize:'0.65rem', color:'rgba(245,239,230,0.5)', letterSpacing:'0.15em', textTransform:'uppercase', margin:'0 0 0.2rem' }}>Aso Ebi Ambassador</p>
          <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.3rem', color:'rgba(245,239,230,0.95)', margin:'0 0 0.25rem', lineHeight:1.2 }}>
            {wedding?.partner1_name} &amp; {wedding?.partner2_name}
          </h1>
          <p style={{ fontSize:'0.78rem', color:'rgba(245,239,230,0.6)', margin:0 }}>
            Welcome, {ambassador.name || 'Ambassador'}
          </p>
        </div>
      </div>

      <div style={{ maxWidth:520, margin:'0 auto', padding:'1rem' }}>

        {/* Summary cards */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginBottom:'1rem' }}>
          <div className="card" style={{ padding:'0.75rem', textAlign:'center' }}>
            <p style={{ fontFamily:'Gibs, serif', fontSize:'1.1rem', color:'#1A1A1A', margin:'0 0 0.15rem' }}>{paidOrders.length}</p>
            <p style={{ fontSize:'0.6rem', color:'#9B9B9B', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', margin:0 }}>Orders</p>
          </div>
          <div className="card" style={{ padding:'0.75rem', textAlign:'center' }}>
            <p style={{ fontFamily:'Gibs, serif', fontSize:'1.1rem', color:'#9BAF9B', margin:'0 0 0.15rem' }}>{paidOrders.filter(o=>o.status==='collected').length}/{paidOrders.length}</p>
            <p style={{ fontSize:'0.6rem', color:'#9B9B9B', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', margin:0 }}>Collected</p>
          </div>
        </div>

        {/* Fabric info */}
        {asoEbi && (
          <div className="card" style={{ padding:'0.875rem 1rem', marginBottom:'1rem' }}>
            <p style={{ fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.35rem' }}>Fabric Details</p>
            <p style={{ fontSize:'0.78rem', color:'#6B6B6B', margin:0, lineHeight:1.6 }}>
              {asoEbi.fabric_name || 'Aso Ebi fabric'} · {cs}{Number(asoEbi.price_per_yard || 0).toLocaleString()}/yard · Min {asoEbi.min_yards || 1} yard{(asoEbi.min_yards || 1) > 1 ? 's' : ''}
              {asoEbi.order_deadline ? ` · Deadline: ${new Date(asoEbi.order_deadline).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}` : ''}
            </p>
          </div>
        )}

        {/* ── Filters: status (row 1) + collection point (row 2), one cohesive bar ── */}
        {(() => {
          const pill = (active, onClick, label, key) => (
            <button key={key} onClick={onClick}
              style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight: active ? 700 : 500,
                color: active ? '#C4826A' : '#6B6B6B',
                background: active ? 'rgba(196,130,106,0.1)' : 'white',
                border: `1.5px solid ${active ? '#C4826A' : '#E5E0D8'}`,
                borderRadius:100, padding:'0.35rem 0.8rem', cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s', display:'inline-flex', alignItems:'center', gap:'0.25rem', flexShrink:0 }}>
              {label}
            </button>
          )
          return (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem', marginBottom:'1rem' }}>
              {/* Status */}
              <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
                {[['all','All'],['collected','✓ Collected'],['not_collected','○ Not collected']]
                  .map(([k,l]) => pill(statusFilter===k, ()=>setStatusFilter(k), l, `st-${k}`))}
              </div>
              {/* Collection point */}
              {points.length > 0 && (
                <div style={{ display:'flex', gap:'0.35rem', overflowX:'auto', scrollbarWidth:'none', paddingBottom:'0.15rem' }}>
                  {pill(pointFilter==='all', ()=>setPointFilter('all'), 'All points', 'pt-all')}
                  {points.map(p => pill(pointFilter===p.name, ()=>setPointFilter(p.name), `📍 ${p.name}`, p.id))}
                </div>
              )}
            </div>
          )
        })()}

        {/* ── Orders ──────────────────────────────────────── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.5rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
            <Package size={14} color="#9B9B9B"/>
            <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#6B6B6B' }}>Orders ({filtered.length})</span>
          </div>
          {orders.length > 0 && (
            <button onClick={exportCSV} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:700, color:'#6B6B6B', background:'white', border:'1.5px solid #E5E0D8', borderRadius:100, padding:'0.3rem 0.75rem', cursor:'pointer' }}>
              ↓ Export CSV
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <p style={{ textAlign:'center', color:'#BCBCBC', padding:'2rem', fontSize:'0.875rem' }}>No orders match this filter.</p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            {filtered.map(order => {
              const paid = isPaid(order)
              return (
                <div key={order.id} className="card" style={{ padding:'0.875rem 1rem' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                    {/* Avatar */}
                    <div style={{ width:36, height:36, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                      background: paid ? 'rgba(155,175,155,0.15)' : '#F3F0EA',
                      border: paid ? '1.5px solid rgba(155,175,155,0.3)' : '1.5px solid transparent' }}>
                      <span style={{ fontFamily:'Gibs, serif', fontSize:'0.9rem', color: paid ? '#7A917A' : '#BCBCBC' }}>
                        {(order.guest_name || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    {/* Details */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {order.guest_name || 'Guest'}
                      </p>
                      <p style={{ fontSize:'0.7rem', color:'#9B9B9B', margin:'1px 0 0' }}>
                        {order.yards || 0} yard{(order.yards || 0) !== 1 ? 's' : ''} · {cs}{Number(order.amount || 0).toLocaleString()}
                      </p>
                    </div>
                    {/* Status + Menu */}
                    <div style={{ display:'flex', alignItems:'center', gap:'0.35rem', flexShrink:0 }}>
                      <span style={{ fontSize:'0.65rem', fontWeight:700,
                        color: order.status === 'collected' ? '#3D8B5E' : paid ? '#7A917A' : '#B36B55',
                        background: order.status === 'collected' ? 'rgba(61,139,94,0.1)' : paid ? 'rgba(155,175,155,0.12)' : 'rgba(179,107,85,0.1)',
                        borderRadius:100, padding:'3px 10px' }}>
                        {STATUS_LABELS[order.status] || order.status || 'Unpaid'}
                      </span>
                      <button onClick={(e) => handleMenu(order.id, e)} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:'#9B9B9B' }}>
                        <MoreVertical size={16}/>
                      </button>
                    </div>
                  </div>
                  {/* Collection point */}
                  {order.collection_point && (
                    <p style={{ fontSize:'0.68rem', color:'#BCBCBC', margin:'0.4rem 0 0 3.1rem' }}>
                      📍 {order.collection_point}
                    </p>
                  )}

                  {/* 3-dot dropdown menu */}
                  {openMenu === order.id && (
                    <>
                      <div onClick={() => setOpenMenu(null)} style={{ position:'fixed', inset:0, zIndex:299 }}/>
                      <div style={{ position:'fixed', top:menuPos.top, right:menuPos.right, background:'white', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:300, minWidth:180, padding:'0.375rem' }}>
                        {[
                          order.status === 'paid'      && { label:'Mark Collected',   action:() => updateStatus(order,'collect') },
                          order.status === 'collected' && { label:'Mark Uncollected', action:() => updateStatus(order,'uncollect') },
                          order.guest_phone && (order.status === 'paid' || order.status === 'collected') && { label:'Remind to Collect', action:() => window.open(waCollect(order.guest_phone, order.guest_name, order.collection_point), '_blank') },
                          order.guest_phone && order.status === 'pending' && { label:'Remind to Pay', action:() => window.open(waLink(order.guest_phone, order.guest_name), '_blank') },
                        ].filter(Boolean).map((item, i) => (
                          <button key={i} onClick={() => { item.action(); setOpenMenu(null) }}
                            style={{ display:'block', width:'100%', textAlign:'left', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:600, color: item.danger ? '#DC2626' : '#1A1A1A', background:'none', border:'none', padding:'0.5rem 0.75rem', borderRadius:'0.5rem', cursor:'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.background='#F8F4F0'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Okoiya branding */}
      <div style={{ textAlign:'center', padding:'1.5rem 1rem 2rem' }}>
        <a href="https://okoiya.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'#F3F0EA', borderRadius:100, padding:'0.4rem 1rem', border:'1px solid #E5E0D8', marginBottom:'0.625rem' }}>
          <span style={{ fontSize:'0.7rem', color:'rgba(3,1,0,0.5)' }}>Made with</span>
          <span style={{ fontFamily:'Gibs, serif', fontSize:'0.8rem', color:'#C4826A' }}>Okoiya</span>
        </a>
        <p style={{ fontSize:'0.72rem', color:'#BCBCBC', margin:'0.4rem 0 0' }}>
          Planning your own wedding?{' '}
          <a href="/signup" style={{ color:'#C4826A', fontWeight:700, textDecoration:'none' }}>Become an Okoiya Couple today →</a>
        </p>
      </div>
    </div>
  )
}
